import React from 'react';
import { useHostFlowActions, type FlowAction } from '../../hooks/useHostFlowActions';
import type { GameMode, RoomPhase } from '@interactive-room';
import { PHASE_STRIP } from '../../theme/designSystem';

interface ContextualHostBarProps {
  phase: RoomPhase;
  mode?: GameMode | null;
  payload: unknown;
  runtime: unknown;
  connectedCount: number;
  hasActivity: boolean;
  setPhase: (phase: RoomPhase) => Promise<void>;
  dispatchActivityAction: (action: { type: string; payload?: unknown }) => Promise<void>;
  extraActions?: React.ReactNode;
}

function ActionButton({ action }: { action: FlowAction }) {
  const className =
    action.variant === 'primary'
      ? 'sala-btn-hero text-xs py-2.5 px-4'
      : action.variant === 'secondary'
        ? 'sala-btn-outline text-xs py-2 px-3'
        : 'rounded-lg px-3 py-2 text-xs font-semibold text-[var(--sala-ink-muted)] hover:text-[var(--sala-ink)]';

  return (
    <button
      type="button"
      className={className}
      disabled={action.disabled}
      onClick={action.onClick}
      title={action.hint}
    >
      {action.label}
    </button>
  );
}

export function ContextualHostBar({
  phase,
  mode,
  payload,
  runtime,
  connectedCount,
  hasActivity,
  setPhase,
  dispatchActivityAction,
  extraActions,
}: ContextualHostBarProps) {
  const actions = useHostFlowActions({
    phase,
    mode,
    payload,
    runtime,
    connectedCount,
    hasActivity,
    setPhase,
    dispatchActivityAction,
  });

  const phaseMeta = PHASE_STRIP[phase];
  const isLive = phase === 'ACTIVE';

  return (
    <div className="sala-glass flex flex-wrap items-center gap-3 px-3 py-2">
      <div
        className={`sala-phase-badge flex min-w-0 items-center gap-2 border-r border-[var(--sala-border)] pr-3${isLive ? ' sala-phase-badge--live' : ''}`}
      >
        <span
          className={`sala-phase-badge__dot h-2 w-2 shrink-0 rounded-full${isLive ? ' sala-phase-badge__dot--pulse' : ''}`}
          style={isLive ? undefined : { background: phaseMeta.color }}
        />
        <div className="min-w-0">
          <p
            className="sala-phase-badge__label font-display text-xs font-bold uppercase tracking-wide"
            style={isLive ? undefined : { color: phaseMeta.color }}
          >
            {phaseMeta.label}
          </p>
          <p className="hidden truncate text-[10px] text-[var(--sala-ink-dim)] sm:block">{phaseMeta.description}</p>
        </div>
      </div>

      <div className="flex flex-1 flex-wrap items-center gap-2">
        {actions.map((action) => (
          <ActionButton key={action.id} action={action} />
        ))}
      </div>

      {extraActions && <div className="flex shrink-0 gap-2">{extraActions}</div>}
    </div>
  );
}
